import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from '@app/app.component';
import { appConfig } from '@app/app.config';
import { captureResetTokenFromUrl } from '@core/utils/reset-token';
import { environment } from '@environments/environment';
import * as Sentry from '@sentry/angular';
import { Settings as LuxonSettings } from 'luxon';

// First statement in the file on purpose: the password-reset token has to leave the URL
// before Sentry, the Meta pixel, Mixpanel or the router can read it. See the util.
captureResetTokenFromUrl();

// A27. The app's display locale is en-US by design (CONSTANTS.LOCALE), but Luxon's
// locale-dependent formats (`monthLong` and friends) followed the *machine's* locale —
// a German system rendered "August" pages differently, which is how a spec first caught it.
// One line makes every Luxon format agree with the rest of the app.
LuxonSettings.defaultLocale = 'en-US';

// A7. Swiper is NOT registered here anymore: the eager `swiper/element/bundle`
// import cost 180 kB of `main` for carousels that live on lazy screens. The
// registration happens in SwiperDirective on first use — custom-element
// upgrades are retroactive, so the already-rendered <swiper-container> picks
// it up the moment the chunk lands.

if (environment.production) {
  Sentry.init({
    dsn: environment.sentryDSN,
    // A21 (decides what A7 measured): the SDK core and tracing stay eager —
    // that is what catches boot crashes and the pageload transaction. Replay
    // is NOT in this list: bundled eagerly it cost 78 kB of initial transfer,
    // and a same-bundle dynamic import does not split it (esbuild assigns the
    // module to the static graph the moment anything references it). It is
    // fetched below from Sentry's CDN instead.
    integrations: [Sentry.browserTracingIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/my\.pocketguard\.com/,
      /^https:\/\/app\.pocketguard\.com/,
    ],
    // Session Replay (client options read by the integration whenever it lands)
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  // A21. Session replay arrives from Sentry's own CDN after init — 78 kB of
  // transfer off every visitor's critical path. The trade, recorded here: a
  // client that blocks Sentry's CDN records no replays (errors are still
  // captured by the eager core), and the first moments before the fetch
  // resolves are not in the recording.
  Sentry.lazyLoadIntegration('replayIntegration')
    .then((replayIntegration) => {
      Sentry.addIntegration(replayIntegration());
    })
    .catch((): undefined => undefined);
}

bootstrapApplication(AppComponent, appConfig).catch((err) =>
  console.error(err)
);
